/**
 * Couple-side moderation for all guest-book messages across every
 * wedding-party member's mini-page. /dashboard/party-messages
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, RefreshCw, Trash2 } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

export default function ManagePartyMessagesPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  async function load() {
    setBusy(true);
    try {
      const { data } = await api.get(`/weddings/me/party-messages?status=${filter}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function setHidden(id, hide) {
    try {
      await api.patch(`/party-messages/${id}/${hide ? "hide" : "restore"}`);
      toast.success(hide ? "Message hidden" : "Message restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const stats = useMemo(() => ({
    total: items.length,
    hidden: items.filter((m) => m.is_hidden).length,
    visible: items.filter((m) => !m.is_hidden).length,
  }), [items]);

  return (
    <div data-testid="manage-party-messages">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Roster Inbox</p>
          <h1 className="font-serif font-light text-4xl">Wedding-Party Messages</h1>
          <p className="text-[var(--muted)] text-sm mt-2">Every note guests have left on your wedding-party members' mini-pages. Hide anything you'd rather not show.</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs border border-[var(--line)] rounded px-3 py-1.5" data-testid="manage-filter">
            <option value="all">All ({stats.total})</option>
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
          </select>
          <button onClick={load} className="btn-outline-gold text-xs" data-testid="manage-refresh">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-12 text-center">
          <p className="font-serif text-2xl mb-2">No messages {filter !== "all" ? `(${filter})` : "yet"}</p>
          <p className="text-[var(--muted)] text-sm mb-4">Once friends drop notes on your wedding-party Roster pages, they'll show up here.</p>
          <Link to="/dashboard/wedding-party" className="btn-gold">Open wedding party →</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => (
            <div
              key={m.message_id}
              data-testid={`manage-msg-${m.message_id}`}
              className={`bg-white border rounded-2xl p-5 flex gap-4 ${m.is_hidden ? "border-red-200 opacity-70" : "border-[var(--line)]"}`}
            >
              {m.photo_url && (
                <img
                  src={`${BACKEND_URL}${m.photo_url}`}
                  alt=""
                  className="w-24 h-24 rounded-xl object-cover shrink-0"
                  data-testid={`manage-photo-${m.message_id}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1">
                  <p className="font-medium">{m.from_name}</p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">→ {m.member_name || "(deleted member)"}</p>
                  {m.is_hidden && <span className="text-[10px] uppercase tracking-[0.18em] text-red-500">Hidden</span>}
                </div>
                <p className="text-sm text-[var(--ink)] mb-2">{m.message}</p>
                <p className="text-[10px] text-[var(--muted)]">{(m.created_at || "").replace("T", " ").slice(0, 16)}</p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {m.is_hidden ? (
                  <button onClick={() => setHidden(m.message_id, false)} data-testid={`restore-${m.message_id}`} className="btn-outline-gold text-xs">
                    <Eye className="w-3 h-3" /> Restore
                  </button>
                ) : (
                  <button onClick={() => setHidden(m.message_id, true)} data-testid={`hide-${m.message_id}`} className="btn-outline-gold text-xs !text-red-500 !border-red-200">
                    <EyeOff className="w-3 h-3" /> Hide
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
